'use client';

import type {
  ProductPageInfoFragment,
  ProductPageInfoUspFragment,
} from '@business/gql/graphql';
import { Dialog, Portal } from '@headlessui/react';
import { XIcon } from '@ngg/icons';
import { cn } from '@ngg/storefront-utils';
import { RichText } from '@ui/components/RichText';
import {
  ChevronIcon,
  DiscountIcon,
  PackageIcon,
  ReturnIcon,
  TagIcon,
} from '@ui/icons';
import { ComponentPropsWithoutRef, useState } from 'react';

const Icons = {
  Discount: DiscountIcon,
  Tag: TagIcon,
  Package: PackageIcon,
  Return: ReturnIcon,
};

type UspIcon = keyof typeof Icons;

function UspIcon({
  icon,
  ...props
}: { icon?: UspIcon | null } & ComponentPropsWithoutRef<'svg'>) {
  if (typeof icon !== 'string') return null;

  const Icon = Icons[icon];

  return <Icon {...props} />;
}

export default function ProductUSP({
  uspsCollection,
  className,
}: {
  uspsCollection?: ProductPageInfoFragment['uspsCollection'] | null;
  className?: string;
}) {
  const [popup, setPopup] = useState<ProductPageInfoUspFragment['popup']>(null);
  if (!uspsCollection?.items.length) return null;
  return (
    <>
      <ul
        className={cn(
          'grid grid-flow-col-dense grid-cols-2 grid-rows-4 justify-items-start border py-2 font-sans text-sm @xl:py-4',
          className,
        )}>
        {uspsCollection.items.map(
          (item) =>
            item?.__typename === 'Usp' && (
              <li
                key={item?.sys.id}
                onClick={() => (item?.popup ? setPopup(item.popup) : null)}
                className="col-span-2 flex w-full items-center overflow-hidden px-4 py-2 text-black @xl:col-span-1 @xl:row-span-2 @xl:px-6 hover:underline"
                role={item?.popup ? 'button' : 'listitem'}>
                <UspIcon
                  icon={item?.icon as UspIcon}
                  className="mr-2 shrink-0"
                />
                <RichText
                  content={item?.content}
                  className="overflow-hidden text-sm text-black [&>p]:overflow-hidden [&>p]:text-ellipsis [&>p]:whitespace-nowrap"
                />
                <ChevronIcon className="ml-1 mt-1 shrink-0" />
              </li>
            ),
        )}
      </ul>
      <Portal>
        <Dialog
          open={Boolean(popup)}
          onClose={() => setPopup(null)}
          className="relative z-40">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel
              className={cn(
                'relative mx-auto max-h-full max-w-xl items-start overflow-auto bg-main px-4 py-10 text-left md:px-6',
              )}>
              <button
                type="button"
                onClick={() => setPopup(null)}
                className="absolute right-0 top-0 p-4">
                <XIcon className="text-base" />
              </button>
              <RichText content={popup} className="text-sm" />
            </Dialog.Panel>
          </div>
        </Dialog>
      </Portal>
    </>
  );
}
